import React, { useEffect, useRef, useState } from 'react';
import { MoonLoader } from 'react-spinners';
import { Document, Page, pdfjs } from 'react-pdf';
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.js?url';

import { magazineService } from '@project/services';

pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

type ProtectedPdfViewerProps = {
  magazineId: number;
};

export const ProtectedPdfViewer: React.FC<ProtectedPdfViewerProps> = ({ magazineId }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [fileUrl, setFileUrl] = useState('');
  const [numPages, setNumPages] = useState(0);
  const [pageWidth, setPageWidth] = useState<number>();
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let objectUrl = '';

    setFileUrl('');
    setHasError(false);

    magazineService
      .getMagazineContent(magazineId)
      .then((blob) => {
        if (!isMounted) return;
        objectUrl = URL.createObjectURL(blob);
        setFileUrl(objectUrl);
      })
      .catch(() => isMounted && setHasError(true));

    return () => {
      isMounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [magazineId]);

  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        setPageWidth(Math.min(containerRef.current.clientWidth - 24, 900));
      }
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const blockContextMenu = (event: React.MouseEvent) => event.preventDefault();

  const Loader = (
    <div className="flex h-full items-center justify-center py-20">
      <MoonLoader color="#B58B00" />
    </div>
  );

  return (
    <div
      ref={containerRef}
      onContextMenu={blockContextMenu}
      className="h-full w-full select-none overflow-y-auto py-4"
    >
      {hasError ? (
        <div className="flex h-full items-center justify-center text-sm text-gray-500">
          Unable to load this magazine. Please try again.
        </div>
      ) : !fileUrl ? (
        Loader
      ) : (
        <Document
          file={fileUrl}
          loading={Loader}
          onLoadSuccess={({ numPages: total }) => setNumPages(total)}
          onLoadError={() => setHasError(true)}
          onSourceError={() => setHasError(true)}
          error={
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              Unable to load this magazine. Please try again.
            </div>
          }
        >
          {Array.from({ length: numPages }, (_, index) => (
            <div key={`page-${index + 1}`} className="mb-4 flex justify-center shadow">
              <Page
                pageNumber={index + 1}
                width={pageWidth}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </div>
          ))}
        </Document>
      )}
    </div>
  );
};
